import api from '../api';
import { isUserAnalyticsEnabled } from '../configuration/config';

interface UserAnalyticsEvent {
  path: string;
  timeOnPage?: number;
  loadDuration?: number;
  event: 'timeOnPage' | 'loadDuration' | 'pageView';
  timestamp: string;
  userId: string;
  apiMetrics?: {
    totalApiRequests: number;
    avgApiDuration: number;
    apiErrorRate: number;
  };
}

class UserAnalyticsService {
  private static instance: UserAnalyticsService;
  private pageEventsQueue: UserAnalyticsEvent[] = [];
  private batchSize = 10; // Smaller batch size for page events
  private flushInterval = 30000; // 15 seconds
  private flushTimer: NodeJS.Timeout | null = null;

  private constructor() {
    this.startBatchFlush();
  }

  public static getInstance(): UserAnalyticsService {
    if (!UserAnalyticsService.instance) {
      UserAnalyticsService.instance = new UserAnalyticsService();
    }
    return UserAnalyticsService.instance;
  }

  public queuePageEvent(event: UserAnalyticsEvent) {
    this.pageEventsQueue.push(event);

    if (this.pageEventsQueue.length >= this.batchSize) {
      this.flushEvents();
    }
  }

  private async flushEvents() {
    if (this.pageEventsQueue.length === 0) return;

    const eventsToSend = [...this.pageEventsQueue];
    this.pageEventsQueue = [];

    const isEnabled = await isUserAnalyticsEnabled();
    if (!isEnabled) {
      return;
    }

    try {
      await this.sendBatchedEvents('/Analytics/page-events', eventsToSend);
    } catch (error) {
      console.warn('Failed to send page analytics batch:', error);
    }
  }

  private async sendBatchedEvents(endpoint: string, events: UserAnalyticsEvent[]) {
    await api.post(
      endpoint,
      JSON.stringify({
        events: events,
        totalEvents: events.length,
        timestamp: new Date().toISOString(),
      }),
    );
  }

  private startBatchFlush() {
    this.flushTimer = setInterval(() => {
      this.flushEvents();
    }, this.flushInterval);
  }

  public destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.flushEvents(); // Flush any remaining events
  }

  // Force flush (useful for page unload)
  public forceFlush() {
    return this.flushEvents();
  }
}

export default UserAnalyticsService;
